// Dynamic Swiper system -- frontend registry.
//
// Mirror of server_elakay/elakay_helpers/dynamicSwiperRegistry.js (limits,
// supported pages, slot grammar). The backend always re-validates; this copy
// exists so the UI can explain a rule before a request is sent and so every
// page/renderer shares one definition instead of scattered if/else.

export const DYNAMIC_SWIPER_LIMITS = Object.freeze({
  MIN_SLIDES: 1,
  // Platform ceiling. Each user's EFFECTIVE limit comes from Animation
  // Settings -> Swiper Access & Limits (useSwiperAccess / the swiper's own
  // `limits` from the API); 3 by default.
  MAX_SLIDES: 10,
  DEFAULT_MAX_SLIDES: 3,
  // Security ceiling for the original. The backend creates swiper-specific
  // delivery variants after validating the real image bytes.
  MAX_IMAGE_MB: 25,
  MAX_IMAGE_BYTES: 25 * 1024 * 1024,
  MAX_PLACEMENTS_PER_PAGE: 9,
  NAME_MAX: 120,
  DESCRIPTION_MAX: 500,
  ALT_MAX: 200,
  TITLE_MAX: 120,
  SUBTITLE_MAX: 240,
  SLIDE_DESCRIPTION_MAX: 300,
  BUTTON_LABEL_MAX: 40,
  MIN_ASPECT_RATIO: 0.5,
  MAX_ASPECT_RATIO: 8,
});

// pageKey = the PAGE_ID each page already passes to DynamicPageRenderer.
// Display labels are translated in SwiperAdminUI.jsx (mainPageLabel).
// AAA/AAB/AAC are category TEMPLATES (shared by many categories) and are
// intentionally absent: category swipers are placed per category id.
export const MAIN_PAGES = Object.freeze([
  { pageKey: "HOME", route: "/" },
  { pageKey: "newProducts", route: "/new-product" },
  { pageKey: "bestSellers", route: "/best-sellers" },
  { pageKey: "dealTodays", route: "/deal-today" },
  { pageKey: "productSearch", route: "/search" },
  { pageKey: "userView", route: "/user-viewed" },
  { pageKey: "searchHistoryAllCards", route: "/search-history" },
  { pageKey: "viewingActivityAllCards", route: "/viewing-activity" },
  { pageKey: "shopsYouFollowAllCards", route: "/shops-you-follow" },
  { pageKey: "USER_PROFILE", route: "/profile" },
  { pageKey: "ORDERS", route: "/c/orders" },
  { pageKey: "CART", route: "/cart" },
]);

const MAIN_PAGE_KEYS = new Set(MAIN_PAGES.map((page) => page.pageKey));
export const CATEGORY_TEMPLATE_PAGE_KEYS = Object.freeze(["AAA", "AAB", "AAC"]);

export const isSwiperMainPage = (pageKey) => MAIN_PAGE_KEYS.has(pageKey);

// Which page a DynamicPageRenderer instance should load swipers for.
//   swiperPage === false      -> explicitly none
//   swiperPage is an object   -> that page (category pages pass their id)
//   otherwise                 -> the renderer's own pageId, if registered
export const resolveSwiperPage = (swiperPage, pageId) => {
  if (swiperPage === false || swiperPage === null) return null;
  if (swiperPage && typeof swiperPage === "object") {
    if (swiperPage.pageType === "category") {
      return swiperPage.categoryId ? { pageType: "category", categoryId: String(swiperPage.categoryId) } : null;
    }
    if (swiperPage.pageType === "main" && isSwiperMainPage(swiperPage.pageKey)) {
      return { pageType: "main", pageKey: swiperPage.pageKey };
    }
    return null;
  }
  return isSwiperMainPage(pageId) ? { pageType: "main", pageKey: pageId } : null;
};

export const pageCacheKey = (page) => (page
  ? (page.pageType === "category" ? `category:${page.categoryId}` : `main:${page.pageKey}`)
  : "");

// ─── Slots ───────────────────────────────────────────────────────────────

export const SLOT_TOP = "top";
export const SLOT_BOTTOM = "bottom";
// A category's advertising banner (My Global Pub): drawn by the Layout where
// the category's pub slider is -- never between the page sections.
export const SLOT_BANNER = "banner";
const SLOT_KEY_PATTERN = /^(?:top|bottom|banner|after:[A-Za-z][A-Za-z0-9_]{0,63})$/;

export const slotAfter = (sectionId) => `after:${sectionId}`;
export const isValidSlotKey = (slotKey) => typeof slotKey === "string" && SLOT_KEY_PATTERN.test(slotKey);
export const parseSlotKey = (slotKey) => {
  if (slotKey === SLOT_BANNER) return { type: "banner", sectionId: null };
  if (slotKey === SLOT_TOP) return { type: "top", sectionId: null };
  if (slotKey === SLOT_BOTTOM) return { type: "bottom", sectionId: null };
  if (typeof slotKey === "string" && slotKey.startsWith("after:")) return { type: "after", sectionId: slotKey.slice(6) };
  return { type: "unknown", sectionId: null };
};

// Ordered, linear list of every insertion point of a page: before the first
// section, after each section, end of page.
export const buildPageSlots = (sectionIds = []) => [
  SLOT_TOP,
  ...sectionIds.map(slotAfter),
  SLOT_BOTTOM,
];

// Groups placements (already sorted by position) by slot. A placement whose
// section is not on the page anymore is an "orphan": still rendered, at the
// end of the page, instead of disappearing silently.
export const groupPlacementsBySlot = (placements = [], sectionIds = []) => {
  const known = new Set(sectionIds);
  const bySlot = new Map();
  const orphans = [];
  [...placements]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .forEach((placement) => {
      const { type, sectionId } = parseSlotKey(placement.slotKey);
      // The banner is the Layout's, not an orphan of the sections.
      if (type === "banner") {
        if (!bySlot.has(SLOT_BANNER)) bySlot.set(SLOT_BANNER, []);
        bySlot.get(SLOT_BANNER).push(placement);
        return;
      }
      if (type === "unknown" || (type === "after" && !known.has(sectionId))) {
        orphans.push(placement);
        return;
      }
      if (!bySlot.has(placement.slotKey)) bySlot.set(placement.slotKey, []);
      bySlot.get(placement.slotKey).push(placement);
    });
  return { bySlot, orphans };
};

// Same cascade as each page's own _extractFromSettings(): enabled sections
// sorted by order, else a facets[] list, else null.
export const extractSectionIds = (settings) => {
  if (!settings || typeof settings !== "object") return null;
  if (Array.isArray(settings.sections) && settings.sections.length > 0) {
    const ids = [...settings.sections]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((section) => section?.sectionId && section.enabled !== false)
      .map((section) => section.sectionId);
    if (ids.length) return ids;
  }
  if (Array.isArray(settings.facets) && settings.facets.length > 0) return settings.facets.filter(Boolean);
  return null;
};

export const firstSectionIds = (...candidates) => {
  for (const candidate of candidates) {
    const ids = Array.isArray(candidate) ? candidate.filter(Boolean) : extractSectionIds(candidate);
    if (ids && ids.length) return ids;
  }
  return [];
};

// ─── Slides / settings ───────────────────────────────────────────────────

export const CONTENT_POSITIONS = Object.freeze([
  "top-left", "top-center", "top-right",
  "center-left", "center", "center-right",
  "bottom-left", "bottom-center", "bottom-right",
]);
export const TEXT_THEMES = Object.freeze(["light", "dark"]);
export const BUTTON_STYLES = Object.freeze(["primary", "light", "dark", "outline"]);
export const BUTTON_TARGETS = Object.freeze(["_self", "_blank"]);
export const TRANSITIONS = Object.freeze(["slide", "fade"]);
export const IMAGE_FITS = Object.freeze(["cover", "contain"]);
export const IMAGE_POSITIONS = Object.freeze(["center", "top", "bottom", "left", "right"]);
// "auto": gradient behind the text only (default) · "none" · "full": even
// tint over the whole image.
export const SLIDE_OVERLAYS = Object.freeze(["auto", "none", "full"]);

const OBJECT_POSITIONS = Object.freeze({
  center: "50% 50%",
  top: "50% 0%",
  bottom: "50% 100%",
  left: "0% 50%",
  right: "100% 50%",
});

// CSS for one slide's image inside the swiper frame. Never stretches: the
// image keeps its own proportions (cover crops, contain adds bands) and the
// anchor decides which part stays visible. Slides saved before per-slide
// fit existed use the swiper-wide settings.imageFit.
export const slideImageStyle = (slide, settings) => {
  const fit = IMAGE_FITS.includes(slide?.imageFit) ? slide.imageFit : (settings?.imageFit === "contain" ? "contain" : "cover");
  return { objectFit: fit, objectPosition: OBJECT_POSITIONS[slide?.imagePosition] || OBJECT_POSITIONS.center };
};
// Same, with rounded corners (radius = a CSS border-radius, "" = square).
// Cover: the image fills the frame, so its corners are the frame's. Contain:
// the element is given the EXACT rectangle of the visible picture (frame /
// image ratios + the slide's anchor), so the picture itself is rounded and
// the bands around it stay untouched.
const IMAGE_ANCHORS = Object.freeze({ center: [0.5, 0.5], top: [0.5, 0], bottom: [0.5, 1], left: [0, 0.5], right: [1, 0.5] });
const round3 = (value) => Math.round(value * 1000) / 1000;
export const slideImageBoxStyle = (slide, settings, frameRatio, radius) => {
  const base = slideImageStyle(slide, settings);
  if (!radius) return base;
  const imageRatio = slide?.width && slide?.height ? slide.width / slide.height : 0;
  if (base.objectFit !== "contain" || !imageRatio || !(frameRatio > 0)) return { ...base, borderRadius: radius };
  let width = 100;
  let height = 100;
  if (imageRatio > frameRatio) height = (frameRatio / imageRatio) * 100;
  else width = (imageRatio / frameRatio) * 100;
  const [anchorX, anchorY] = IMAGE_ANCHORS[slide.imagePosition] || IMAGE_ANCHORS.center;
  return {
    objectFit: "cover",
    objectPosition: "50% 50%",
    right: "auto",
    bottom: "auto",
    left: `${round3((100 - width) * anchorX)}%`,
    top: `${round3((100 - height) * anchorY)}%`,
    width: `${round3(width)}%`,
    height: `${round3(height)}%`,
    borderRadius: radius,
  };
};
export const UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";

export const DEFAULT_SWIPER_SETTINGS = Object.freeze({
  autoplay: true,
  autoplayDelay: 5000,
  pauseOnHover: true,
  loop: true,
  transition: "slide",
  speed: 600,
  showPagination: true,
  showArrowsDesktop: true,
  showArrowsMobile: false,
  imageFit: "cover",
  backgroundColor: "#f1f5f9",
  maxHeightDesktop: 600,
  maxHeightMobile: null,
  borderRadius: 12,
  // Navigation / interaction choices (2026-10-15): null = not chosen, the
  // renderer keeps its historical behaviour (see DynamicSwiper).
  paginationType: null,
  dynamicBullets: null,
  keyboard: null,
  allowTouchMove: null,
  rewind: null,
  disableOnInteraction: null,
  stopOnLastSlide: null,
  // Advanced display (swiperDisplayModel.js): null = never configured.
  display: null,
  // Corners of every slide / every slide image (null = square). A slide's
  // own cornerRadius / imageRadius wins.
  slideRadius: null,
  imageRadius: null,
});

export const PAGINATION_TYPES = Object.freeze(["bullets", "fraction", "progressbar"]);
export const CONTROL_SETTING_KEYS = Object.freeze([
  "paginationType", "dynamicBullets", "keyboard", "allowTouchMove", "rewind", "disableOnInteraction", "stopOnLastSlide",
]);

const UPLOAD_EXTENSIONS = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
  "image/svg+xml": [".svg"],
};

// null when the file can be sent, else "type" | "size". The server
// re-checks both (and the real bytes) -- this only avoids a useless upload.
export const getSwiperImageFileError = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const extensions = UPLOAD_EXTENSIONS[file?.type];
  if (!extensions || !extensions.some((extension) => name.endsWith(extension))) return "type";
  if (file.size > DYNAMIC_SWIPER_LIMITS.MAX_IMAGE_BYTES) return "size";
  return null;
};

export const formatMegabytes = (bytes) => `${(Number(bytes || 0) / (1024 * 1024)).toFixed(2)} MB`;

// eslint-disable-next-line no-control-regex
const UNSAFE_URL_CHARS = /[\u0000- \u007F<>"'`\\]/;

// Client-side guard used before RENDERING a button (defense in depth: the
// backend already refused anything else when it was saved).
export const isSafeButtonUrl = (url) => {
  const value = typeof url === "string" ? url.trim() : "";
  if (!value || UNSAFE_URL_CHARS.test(value)) return false;
  if (value.startsWith("/")) return !value.startsWith("//");
  try {
    const parsed = new URL(value);
    return (parsed.protocol === "http:" || parsed.protocol === "https:") && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
};

export const isInternalPath = (url) => typeof url === "string" && url.startsWith("/") && !url.startsWith("//");

// Quick pre-check for the "Elakay image URL" source; the backend decides.
export const looksLikeElakayImageUrl = (url) => {
  const value = typeof url === "string" ? url.trim() : "";
  if (!value || UNSAFE_URL_CHARS.test(value)) return false;
  if (/^\/elakay_api\/(?:img|pubCi|pubR|image-assets|img-load)(?:\/|\?|$)/.test(value)) return true;
  if (/^\/data\/images\//.test(value)) return true;
  try {
    const parsed = new URL(value);
    if (!["http:", "https:"].includes(parsed.protocol)) return false;
    return /\/elakay_api\/(?:img|pubCi|pubR|image-assets|img-load)(?:\/|\?|$)/.test(`${parsed.pathname}${parsed.search}`)
      || /^\/data\/images\//.test(parsed.pathname);
  } catch {
    return false;
  }
};

export const ratioLabel = (width, height) => {
  if (!width || !height) return "";
  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const divisor = gcd(Math.round(width), Math.round(height)) || 1;
  const w = Math.round(width / divisor);
  const h = Math.round(height / divisor);
  if (w <= 32 && h <= 32) return `${w}:${h}`;
  return `${(width / height).toFixed(2)}:1`;
};

// "3.2:1" style companion of ratioLabel (1600 x 500 -> 16:5 ≈ 3.2:1).
export const ratioDecimalLabel = (width, height) => {
  if (!width || !height) return "";
  return `${Number((width / height).toFixed(2))}:1`;
};

// Moves one item of a list to another index (slides reorder by buttons or
// drag & drop). Returns the same list when nothing moves.
export const moveItem = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export const isAspectRatioSupported = (width, height) => {
  if (!width || !height) return false;
  const ratio = width / height;
  return ratio >= DYNAMIC_SWIPER_LIMITS.MIN_ASPECT_RATIO && ratio <= DYNAMIC_SWIPER_LIMITS.MAX_ASPECT_RATIO;
};
